import { useSettings } from '../../../hooks/useSettings/useSettings';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material';
import { PolicyStreamCard } from './PolicyStreamCard';
import { POLICYSTREAM_SUPPORT_EMAIL } from '../constants';

interface PolicyStreamNeedHelpProps {
  isInHamburgerMenu?: boolean;
}

export const PolicyStreamNeedHelp = ({
  isInHamburgerMenu = false,
}: PolicyStreamNeedHelpProps) => {
  const { settings } = useSettings();
  return (
    <NeedHelpCard>
      <Stack direction={'row'} spacing={1}>
        <StyledHelpIcon />
        <strong>Need Help?</strong>
      </Stack>
      <Typography sx={{ fontSize: '1rem' }}>
        Speak with a licensed insurance advisor —{' '}
        <strong>no pressure, just guidance.</strong>
      </Typography>
      <AllContactContainer>
        {settings.phone ? (
          <ContactInformationContainer
            href={`tel:${settings.phone}`}
            isInHamburgerMenu={isInHamburgerMenu}
          >
            <Typography>{settings.phone}</Typography>
            {isInHamburgerMenu ? <StyledBackArrow /> : <StyledForwardArrow />}
          </ContactInformationContainer>
        ) : null}
        {settings.email ? (
          <ContactInformationContainer
            href={`mailto:${settings.email}`}
            isInHamburgerMenu={isInHamburgerMenu}
          >
            <Typography>{settings.email}</Typography>
            {isInHamburgerMenu ? <StyledBackArrow /> : <StyledForwardArrow />}
          </ContactInformationContainer>
        ) : (
          <ContactInformationContainer
            href={`mailto:${POLICYSTREAM_SUPPORT_EMAIL}`}
            isInHamburgerMenu={isInHamburgerMenu}
          >
            <Typography>{POLICYSTREAM_SUPPORT_EMAIL}</Typography>
            {isInHamburgerMenu ? <StyledBackArrow /> : <StyledForwardArrow />}
          </ContactInformationContainer>
        )}
        {settings.customButtonList
          ? settings.customButtonList.map((someCustomButton) => (
              <ContactInformationContainer
                href={someCustomButton.url}
                target='_blank'
                isInHamburgerMenu={isInHamburgerMenu}
                key={someCustomButton.title}
              >
                <Typography>{someCustomButton.title}</Typography>
                {isInHamburgerMenu ? (
                  <StyledBackArrow />
                ) : (
                  <StyledForwardArrow />
                )}
              </ContactInformationContainer>
            ))
          : null}
      </AllContactContainer>
    </NeedHelpCard>
  );
};

const StyledHelpIcon = styled(HelpOutlineIcon)(({ theme }) => {
  return { color: theme.palette.primary.main };
});

const StyledBackArrow = styled(ArrowBackIcon)(({ theme }) => {
  return { color: theme.palette.primary.main };
});

const StyledForwardArrow = styled(ArrowForwardIcon)(({ theme }) => {
  return { color: theme.palette.primary.main };
});

const AllContactContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(0.5),
    width: '100%',
  };
});

const ContactInformationContainer = styled('a')<{
  isInHamburgerMenu?: boolean;
}>(({ theme, isInHamburgerMenu }) => {
  return {
    display: 'flex',
    flexDirection: isInHamburgerMenu ? 'row-reverse' : 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    textDecoration: 'none',
    color: 'inherit',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `solid 1px transparent`,
    transition: 'box-shadow 0.3s ease-in-out, border-color 0.3s ease-in-out',
    '&:hover': {
      // @ts-expect-error
      boxShadow: theme.customShadows.primary,
      border: `solid 1px ${theme.palette.primary.main}`,
    },
  };
});

export const NeedHelpCard = styled(PolicyStreamCard)(({ theme }) => {
  return {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  };
});
